import CsjBlockRenderer from '@/components/CsjBlockRenderer';
import { authStore } from '@/lib/authStore';
import { withTenant } from '@/lib/tenantImpersonation';
import {
  BookOpen,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Loader2,
  Presentation,
  Sheet,
  Sparkles,
  Table2,
} from 'lucide-react';
import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  var token = authStore.getAccessToken();
  return { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
}

// ── Confidence badge ────────────────────────────────────────────────
function ConfidencePill({ level, score }) {
  var colors = {
    high: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    medium: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    low: 'bg-red-500/10 text-red-500 border-red-500/20',
  };
  var label = level === 'high' ? 'High Confidence' : level === 'medium' ? 'Medium Confidence' : 'Low Confidence';
  var cls = colors[level] || colors['medium'];
  return (
    <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ' + cls}>
      <Sparkles className="h-3 w-3" />
      {label}
      {typeof score === 'number' && <span className="font-mono">{Math.round(score * 100)}%</span>}
    </span>
  );
}

// ── Source chips ────────────────────────────────────────────────────
function SourceChips({ sources }) {
  var [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;
  var visible = expanded ? sources : sources.slice(0, 4);
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-1.5">
        {visible.map(function(src, i) {
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/5 text-xs text-muted-foreground border border-border/30"
            >
              <FileText className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="max-w-[200px] truncate">{src.filename || src.document || src}</span>
              {src.page ? ' p.' + src.page : ''}
            </span>
          );
        })}
        {sources.length > 4 && (
          <button
            onClick={function() { setExpanded(!expanded); }}
            className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs text-primary hover:bg-primary/5 transition-colors"
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3" /> Less</>
            ) : (
              <><ChevronDown className="h-3 w-3" /> +{sources.length - 4} more</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Work product action rail ────────────────────────────────────────
function WorkProductPanel({ jobId, csj }) {
  var [generating, setGenerating] = useState(null);

  async function downloadPptx() {
    if (!jobId && !csj) return;
    setGenerating('pptx');
    try {
      var resp = await fetch(withTenant(API_URL + '/export/v2/pptx'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ job_id: jobId || undefined }),
      });
      if (!resp.ok) throw new Error('PPTX generation failed');
      var blob = await resp.blob();
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = (csj && csj.title ? csj.title : 'queryverse-export') + '.pptx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Export failed');
    } finally {
      setGenerating(null);
    }
  }

  async function downloadChecklist() {
    if (!csj) return;
    setGenerating('xlsx');
    try {
      var checklistBlocks = (csj.blocks || []).filter(function(b) { return b.type === 'checklist'; });
      if (checklistBlocks.length === 0) {
        alert('No checklist blocks found in this response.');
        setGenerating(null);
        return;
      }
      var rows = ['Category\tItem\tMandatory\tRole\tStatus'];
      checklistBlocks.forEach(function(block) {
        (block.items || []).forEach(function(item) {
          rows.push([
            block.title || 'Checklist',
            item.text || '',
            item.mandatory ? 'Yes' : 'No',
            item.role || '',
            'Pending',
          ].join('\t'));
        });
      });
      var content = rows.join('\n');
      var resp = await fetch(withTenant(API_URL + '/export/xlsx'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content: content, title: (csj.title || 'Checklist') + ' — Checklist', format: 'xlsx' }),
      });
      if (!resp.ok) throw new Error('Checklist export failed');
      var blob = await resp.blob();
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = (csj.title ? csj.title : 'checklist') + '.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Export failed');
    } finally {
      setGenerating(null);
    }
  }

  async function downloadDocx() {
    if (!csj) return;
    setGenerating('docx');
    try {
      var content = csj.blocks
        ? csj.blocks.map(function(b) {
            if (b.type === 'section_heading') return '# ' + b.text;
            if (b.type === 'narrative') return b.text;
            if (b.type === 'key_insight') return '**' + b.headline + '**\n' + b.body;
            if (b.type === 'executive_insight') return b.headline + '\n' + (b.bullets || []).join('\n');
            return '';
          }).filter(Boolean).join('\n\n')
        : '';
      var resp = await fetch(withTenant(API_URL + '/export/docx'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content: content, title: csj.title || 'Knowledge Export', format: 'docx' }),
      });
      if (!resp.ok) throw new Error('DOCX generation failed');
      var blob = await resp.blob();
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = (csj && csj.title ? csj.title : 'queryverse-export') + '.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Export failed');
    } finally {
      setGenerating(null);
    }
  }

  var actions = [
    { key: 'pptx', label: 'Slide Deck', icon: Presentation, fn: downloadPptx },
    { key: 'docx', label: 'Word Doc', icon: FileText, fn: downloadDocx },
    { key: 'xlsx', label: 'Checklist', icon: Table2, fn: downloadChecklist },
  ];

  return (
    <div className="mt-4 pt-3 border-t border-border/30">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
        Export Work Product
      </p>
      <div className="flex flex-wrap gap-2">
        {actions.map(function(action) {
          var Icon = action.icon;
          var busy = generating === action.key;
          return (
            <button
              key={action.key}
              onClick={action.fn}
              disabled={!!generating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border/50 bg-secondary/30 hover:bg-secondary/60 hover:border-primary/30 transition-colors disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Icon className="h-3.5 w-3.5 text-primary" />
              )}
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────
export default function CsjResponsePanel({ csj, jobId, retrieval, isAdmin }) {
  if (!csj) return null;

  var confidenceLevel = retrieval && retrieval.confidence_level;
  var confidenceScore = retrieval && retrieval.confidence_score;
  var sources = (csj.sources && csj.sources.length > 0)
    ? csj.sources
    : [];

  return (
    <div className="space-y-1">
      {/* Title / subtitle */}
      {csj.title && (
        <div className="mb-4">
          <h2 className="text-base font-bold leading-snug text-foreground">{csj.title}</h2>
          {csj.subtitle && (
            <p className="text-sm text-muted-foreground mt-0.5">{csj.subtitle}</p>
          )}
        </div>
      )}

      {/* Confidence */}
      {confidenceLevel && (
        <div className="mb-3">
          <ConfidencePill level={confidenceLevel} score={confidenceScore} />
          {isAdmin && retrieval && (
            <span className="ml-2 text-[11px] text-muted-foreground">
              {retrieval.chunks_returned} chunks · {Math.round((retrieval.top_relevance || 0) * 100)}% top relevance
            </span>
          )}
        </div>
      )}

      {/* Blocks */}
      <CsjBlockRenderer blocks={csj.blocks || []} />

      {/* Sources */}
      <SourceChips sources={sources} />

      {/* Export rail */}
      <WorkProductPanel jobId={jobId} csj={csj} />
    </div>
  );
}
